import React, { ReactElement, useEffect, useState } from 'react';
import { Dropdown, SalvProgressCircle } from '@salvapp/ui-components';

import './ScreeningAlertFilters.scss';
import ScreeningAlertFiltersModal from './ScreeningAlertFiltersModal';
import {
  ApiScreeningAlertFilter,
  ApiScreeningAlertFilterExpressionFieldTypeEnum,
  customListApi,
  screeningAlertFilterApi,
} from '../../../apis';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { Popover } from 'react-bootstrap';
import { useQueryUsersBasic } from '../../../users/userActions';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../store/store';
import ScreeningAlertFiltersResults from './ScreeningAlertFiltersResults';
import { useQueryAttributeMappings } from '../../../schema/mappings/mappingsActions';
import { useLocation, useNavigate } from 'react-router';
import RmsPopover from '../../../common/popover/RmsPopover';
import { getFilterObjects, ScreeningAlertFilterField, ScreeningAlertFilterObjects, toDropdownOption } from './helper';
import dayjs from 'dayjs';
import { CAN_DELETE_SCREENING_ALERT_FILTER } from '../../../auth/permissions';
import Has from '../../../auth/Has';
import { fetchAlertStatuses } from '../../alert-status/alertStatusesActions';
import { InternalApiCustomList, InternalApiEntityType } from '../../../api-internal';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';
import { Spinner } from '../../../feedback/Spinner';
import Tracker from '../../../tracking/tracker';
import { useQueryEntitySchema } from '../../../schema/entitySchemaActions';
import { useMe } from '../../../organisation/useMe';
import SalvPage from '../../../common/SalvPage';
import DataLoadingErrorFallback from '../../../common/errors/DataLoadingErrorFallback';

enum LoadingStatus {
  Loading,
  Done,
  Error,
}

export const ScreeningAlertFilters = (): ReactElement | null => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const { features, currentOrganisation } = useMe();
  const [filters, setFilters] = useState<ApiScreeningAlertFilter[]>([]);
  const [screeningLists, setScreeningLists] = useState<InternalApiCustomList[]>([]);
  const [loading, setLoading] = useState<LoadingStatus>(LoadingStatus.Loading);
  const [refreshFilters, setRefreshFilters] = useState<boolean>(false);

  const { data: personSchema } = useQueryEntitySchema(InternalApiEntityType.PERSON);
  const { data: transactionSchema } = useQueryEntitySchema(InternalApiEntityType.TRANSACTION);
  const adverseMediaEntitiesEnabled = isFeatureEnabled(ORGANISATION_FEATURE.ADVERSE_MEDIA_ENTITIES, features);
  const [screeningAlertFiltersObjects, setScreeningAlertFiltersObjects] = useState<ScreeningAlertFilterObjects>(
    getFilterObjects([], [], undefined, undefined, null, adverseMediaEntitiesEnabled)
  );

  const { statuses } = useSelector((state: AppState) => state.screeningAlertStatuses, shallowEqual);
  const [selectedFilter, setSelectedFilter] = useState<ApiScreeningAlertFilter | undefined>();
  const { data: users } = useQueryUsersBasic();

  useEffect(() => {
    const screeningAlertFiltersObjects = getFilterObjects(
      users,
      statuses,
      personSchema,
      transactionSchema,
      screeningLists,
      adverseMediaEntitiesEnabled
    );
    setScreeningAlertFiltersObjects(screeningAlertFiltersObjects);
  }, [users, statuses, personSchema, transactionSchema, screeningLists, adverseMediaEntitiesEnabled]);

  useEffect(() => {
    if (!refreshFilters) return;

    fetchFilters();
    setRefreshFilters(false);
  }, [refreshFilters]);

  useEffect(() => {
    const fetchLists = async () => {
      const lists = await customListApi.getLists();
      setScreeningLists(lists);
    };

    fetchLists();
  }, []);

  const fetchFilters = async () => {
    let newFilters: ApiScreeningAlertFilter[] = [];
    try {
      newFilters = await screeningAlertFilterApi.findFilters();
      setLoading(LoadingStatus.Done);
    } catch (_) {
      setLoading(LoadingStatus.Error);
    }
    setFilters(newFilters.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)));
  };

  const getExpressionValue = (
    type: ApiScreeningAlertFilterExpressionFieldTypeEnum,
    value: string,
    field: ScreeningAlertFilterField
  ): string => {
    switch (type) {
      case ApiScreeningAlertFilterExpressionFieldTypeEnum.STRING_LIST:
        return JSON.parse(value)
          .map(
            (savedValue: string) =>
              field.options?.find((option) => option.value === savedValue)?.label || toDropdownOption(savedValue)
          )
          .join('; ');
      case ApiScreeningAlertFilterExpressionFieldTypeEnum.TIMESTAMP:
        return dayjs.unix(Number(value) / 1000).format('YYYY-MM-DD');
      default:
        return value;
    }
  };

  function advancedFilterOverlay() {
    return (
      <Popover id='advanced-filter-popover' className='advanced-filter-popover'>
        <Popover.Body>
          {selectedFilter?.expressions.map((expression, index) => {
            const exp = screeningAlertFiltersObjects.getObject(expression.target);
            if (!exp) {
              return <></>;
            }
            const field = exp.getField(expression.field);
            if (!field) {
              return <></>;
            }
            let operation: DropdownOption;
            try {
              operation = field.getOperation(expression.operation);
            } catch (e: unknown) {
              // User tried to load filter that contains NOT expression but the feature is not enabled
              if (e instanceof Error) {
                const errorMessage = `Failed to load filter <${selectedFilter?.id}> for organisation <${currentOrganisation?.id}>: ${e.message}`;
                Tracker.error(errorMessage);
              } else {
                Tracker.error(e);
              }
              return;
            }
            const value = getExpressionValue(expression.fieldType, expression.value, field);
            return (
              <div key={index} className='expression-explanation'>
                {exp.value.label} {field.value.label.toLowerCase()}{' '}
                <b>
                  {operation.label} {value}
                </b>
              </div>
            );
          })}
        </Popover.Body>
      </Popover>
    );
  }

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      const filter = filters.find((filter) => filter.id === Number(id));
      filter ? setSelectedFilter(filter) : setSelectedFilter(undefined);
    } else {
      setSelectedFilter(filters[0]);
    }
  }, [searchParams, filters]);

  useEffect(() => {
    if (statuses && statuses.length === 0) {
      dispatch(fetchAlertStatuses());
    }
  }, [statuses, dispatch]);

  const onSubmit = async (filter: ApiScreeningAlertFilter | undefined) => {
    setSelectedFilter(filter);
    setRefreshFilters(true);
    if (filter) {
      navigate({
        search: `?id=${filter.id}`,
      });
    } else {
      await navigate(`/screening/alert-filters`, { replace: true });
    }
  };

  const renderLoadingStatus = () => {
    switch (loading) {
      case LoadingStatus.Loading:
        return (
          <>
            <Spinner loading={true} small={true} />
            Loading
          </>
        );
      case LoadingStatus.Done:
        return <>Nothing to show here yet. Create a filter to begin.</>;
      case LoadingStatus.Error:
        return <>Something went wrong while loading the page, please try again.</>;
    }
  };

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  return (
    <SalvPage title={'Filter screening alerts'}>
      <div className='screening-alert-filters'>
        {selectedFilter ? (
          <Dropdown
            label='Select filter'
            value={
              selectedFilter
                ? {
                    key: selectedFilter.name,
                    label: `${selectedFilter.name} (${selectedFilter.alertCount})`,
                    value: selectedFilter.name,
                  }
                : undefined
            }
            options={filters.map((filter) => ({
              key: filter.name,
              label: `${filter.name} (${filter.alertCount})`,
              value: filter.name,
            }))}
            onChange={(option: DropdownOption[]) => {
              const filter = filters.find((filter) => filter.name === option[0].key);
              setRefreshFilters(true);
              navigate({
                search: `?id=${filter?.id}`,
              });
            }}
          />
        ) : (
          renderLoadingStatus()
        )}
        {selectedFilter && (
          <RmsPopover overlay={advancedFilterOverlay()}>
            <span className='hover-link'>Show filter conditions</span>
          </RmsPopover>
        )}
        <Has allPermissions={CAN_DELETE_SCREENING_ALERT_FILTER}>
          <ScreeningAlertFiltersModal
            selectedFilter={selectedFilter}
            screeningAlertFiltersObjects={screeningAlertFiltersObjects}
            onSubmit={onSubmit}
          />
        </Has>
      </div>

      <br />
      {selectedFilter ? (
        <ScreeningAlertFiltersResults
          filter={selectedFilter}
          mappings={attributeMappings.map}
          searchParams={searchParams}
          reloadFilters={fetchFilters}
        />
      ) : (
        filters.length > 0 && <>Select a filter to see results</>
      )}
    </SalvPage>
  );
};

export default ScreeningAlertFilters;
